import React, { useState, useMemo, useEffect } from "react";
import Dialog, { DialogProps } from "../Dialog";
import Button from "../Button";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import {
  PlayerTalent,
  UserTalentLevelContract,
} from "../../types/talents";

interface UpgradingProgressData {
  startTime: number;
  endTime: number;
  durationMs: number;
  remainingMs: number;
  progress: number;
  instantCost: number;
}

interface ResearchUpgradeDialogProps extends Omit<DialogProps, "children"> {
  talent: PlayerTalent | null;
  nextLevel?: UserTalentLevelContract;
  onUpgrade?: (talentLevelId: number) => Promise<boolean>;
  canUpgrade?: boolean;
  disabledReason?: string;
  onUpgradeSuccess?: () => void;
  parentTalent?: PlayerTalent | null;
  currentResearchBuildingLevel?: number;
  isCurrentlyUpgrading?: boolean;
  upgradingProgress?: UpgradingProgressData | null;
  onInstantUpgrade?: () => void;
  activeResearchTalentId?: number | null;
  activeResearchTalentName?: string;
}

type DialogState = "info" | "requirements" | "researching" | "completed";

const ResearchUpgradeDialog: React.FC<ResearchUpgradeDialogProps> = ({
  open,
  onClose,
  talent,
  nextLevel,
  onUpgrade,
  canUpgrade = false,
  disabledReason,
  onUpgradeSuccess,
  parentTalent,
  currentResearchBuildingLevel = 0,
  isCurrentlyUpgrading = false,
  upgradingProgress,
  onInstantUpgrade,
  activeResearchTalentId,
  activeResearchTalentName,
  ...dialogProps
}) => {
  const { t } = useAppTranslation(["research", "common"]);
  const [dialogState, setDialogState] = useState<DialogState>("info");
  const [isUpgrading, setIsUpgrading] = useState(false);
  const userInfo = useSelector((state: any) => state.userInfo);
  const navigate = useNavigate();

  // Helper function to format remaining time
  const formatRemainingTime = (ms: number): string => {
    if (ms <= 0) return t("research:talents.complete");
    const totalSeconds = Math.floor(ms / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);

    if (days > 0) {
      return t("research:talents.remainingDhm", { days, hours, minutes });
    } else if (hours > 0) {
      return t("research:talents.remainingHm", { hours, minutes });
    } else {
      return t("research:talents.remainingMin", { minutes });
    }
  };

  useEffect(() => {
    if (open) {
      // If talent is currently upgrading, show researching state
      if (isCurrentlyUpgrading && upgradingProgress) {
        setDialogState("researching");
      } else {
        setDialogState("info");
      }
    }
  }, [open, talent?.talentLevelId, nextLevel?.id, isCurrentlyUpgrading, upgradingProgress]);

  // Prepare upgrade data from talent
  const upgradeData = useMemo(() => {
    if (!talent) return null;

    const currentLevel = talent.level;
    const targetLevel = nextLevel ?? null;
    const nextLevelNumber = targetLevel?.level ?? currentLevel + 1;
    const maxLevel = talent.maxLevel ?? 5;
    const isMaxLevel = currentLevel >= maxLevel;

    const targetUpgradeCost = targetLevel?.upgradeCost ?? talent.upgradeCost;
    const upgradeCost =
      typeof targetUpgradeCost === "number"
        ? targetUpgradeCost
        : talent.amount ?? 0;
    const targetInstantCost = targetLevel?.instantUpgradeCost ?? talent.instantUpgradeCost;
    const bloodGemsCost =
      typeof targetInstantCost === "number"
        ? targetInstantCost
        : undefined;
    const cost = {
      cleg: upgradeCost,
      bloodGems: bloodGemsCost,
    };

    // No requirements in v2 API for now
    const requirements: Array<{ name: string; met: boolean }> = [];

    const baseCurrentEffect =
      currentLevel > 0
        ? talent.effect || talent.description
        : t("research:talents.notUnlockedYet");
    const nextEffectText =
      targetLevel?.description ||
      targetLevel?.name ||
      talent.effect ||
      talent.description ||
      t("research:talents.upgradeToSeeEffect");

    return {
      currentLevel,
      nextLevel: nextLevelNumber,
      maxLevel,
      isMaxLevel,
      currentEffect: baseCurrentEffect,
      nextEffect: nextEffectText,
      cost,
      requirements,
      researchTime:
        targetLevel?.duration ?? talent.duration
          ? t("research:talents.minutesShort", {
              value: targetLevel?.duration ?? talent.duration,
            })
          : "--",
      researchProgress: 0,
      totalTime:
        targetLevel?.duration ?? talent.duration
          ? t("research:talents.minutesShort", {
              value: targetLevel?.duration ?? talent.duration,
            })
          : "--",
    };
  }, [talent, nextLevel, t]);

  const canAfford = useMemo(() => {
    if (!upgradeData || !userInfo) return false;
    const cost = upgradeData.cost;
    if (typeof cost.cleg === "number" && userInfo.totalCleg < cost.cleg) {
      return false;
    }
    if (
      typeof cost.bloodGems === "number" &&
      userInfo.totalBloodGem < cost.bloodGems
    ) {
      return false;
    }
    return true;
  }, [upgradeData, userInfo]);

  const isOtherTalentUpgrading = useMemo(() => {
    if (!talent?.talentId || activeResearchTalentId == null) return false;
    return activeResearchTalentId !== talent.talentId;
  }, [activeResearchTalentId, talent?.talentId]);

  // Check if parent talent requirement is met (parent must have at least level 1)
  const isParentRequirementMet = useMemo(() => {
    if (!talent?.parentTalentId) return true; // No parent required
    if (!parentTalent) return false; // Parent not provided
    return parentTalent.level >= 1;
  }, [talent?.parentTalentId, parentTalent]);

  // Check if research building level requirement is met
  const isBuildingRequirementMet = useMemo(() => {
    if (!talent?.researchBuildingRequiredLevel) return true; // No building level required
    return currentResearchBuildingLevel >= talent.researchBuildingRequiredLevel;
  }, [talent?.researchBuildingRequiredLevel, currentResearchBuildingLevel]);

  // Generate the disabled reason message
  const requirementDisabledReason = useMemo(() => {
    if (!talent) return null;

    if (isOtherTalentUpgrading) {
      return activeResearchTalentName
        ? t("research:talents.disabled.researchInProgressNamed", {
            name: activeResearchTalentName,
          })
        : t("research:talents.disabled.researchInProgress");
    }

    // Check building level requirement
    if (!isBuildingRequirementMet && talent.researchBuildingRequiredLevel) {
      return t("research:talents.disabled.researchBuildingRequired", {
        required: talent.researchBuildingRequiredLevel,
        current: currentResearchBuildingLevel,
      });
    }

    // Check parent talent requirement
    if (!isParentRequirementMet && parentTalent) {
      return t("research:talents.disabled.parentRequired", {
        name: parentTalent.name,
      });
    }

    // Check cost
    if (!canAfford && upgradeData) {
      return t("research:talents.disabled.notEnoughResources", {
        amount: upgradeData.cost.cleg,
      });
    }

    return null;
  }, [
    talent,
    isOtherTalentUpgrading,
    activeResearchTalentName,
    isBuildingRequirementMet,
    isParentRequirementMet,
    canAfford,
    parentTalent,
    currentResearchBuildingLevel,
    upgradeData,
    t,
  ]);

  // Combined disabled reason
  const finalDisabledReason = disabledReason || requirementDisabledReason;

  // Determine if all requirements are met to show the button
  const canShowUpgradeButton = useMemo(() => {
    return canAfford && isBuildingRequirementMet && isParentRequirementMet && canUpgrade && !finalDisabledReason;
  }, [
    canAfford,
    isBuildingRequirementMet,
    isParentRequirementMet,
    canUpgrade,
    finalDisabledReason,
  ]);

  const allRequirementsMet = useMemo(() => {
    if (!upgradeData) return false;
    if (!upgradeData.requirements.length) {
      return true;
    }
    return upgradeData.requirements.every((req) => req.met);
  }, [upgradeData]);

  const handleResearch = async () => {
    if (disabledReason) {
      setDialogState("requirements");
      return;
    }

    if (!allRequirementsMet || !canAfford) {
      setDialogState("requirements");
      return;
    }

    if (!talent || !onUpgrade) {
      return;
    }

    setIsUpgrading(true);

    try {
      const targetLevelId = nextLevel?.id ?? talent.talentLevelId;
      const success = await onUpgrade(targetLevelId);
      if (success) {
        setDialogState("completed");
        onUpgradeSuccess?.();
      } else {
        setDialogState("requirements");
      }
    } finally {
      setIsUpgrading(false);
    }
  };

  const handleInstant = () => {
    console.log("Instant upgrade");
  };

  const handleGoToParent = () => {
    if (!parentTalent) return;
    // Close current dialog
    onClose?.();
    // The parent talent card should be clickable in the tree
    // This will just close the dialog so user can click on parent
  };

  const handleGoToResearchBuilding = () => {
    // Close dialog and navigate to village to upgrade research building
    onClose?.();
    navigate('/research#upgrade');
  };

  if (!talent || !upgradeData || !open) return null;

  const {
    currentLevel,
    nextLevel: nextLevelNumber,
    isMaxLevel,
    currentEffect,
    nextEffect,
    cost,
    requirements,
    researchTime,
    researchProgress,
    totalTime,
  } = upgradeData;

  // Info State - Default view
  const renderInfoState = () => (
    <div className="research-upgrade-content">
      {/* Header Section */}
      <div className="research-header">
        <h3 className="research-title dark-yellow">
          {talent.name} ({currentLevel}/{upgradeData.maxLevel})
        </h3>
        <p className="research-subtitle">
          {talent.description || t("research:talents.researchUpgrade")}
        </p>
      </div>

      {/* Progress Section */}
      <div className="research-progress-section">
        <div className="progress-info">
          <span className="dark-yellow">{t("common:labels.current")}:</span>
          <span className="text-white"> {currentEffect}</span>
        </div>
        <img
          src="/assets/images/icons/arrow-right.svg"
          alt="arrow"
          className="progress-arrow"
        />
        <div className="progress-info">
          <span className="text-success fw-bold">
            {t("research:talents.nextLevel")}:
          </span>
          <span className="text-white"> {nextEffect}</span>
        </div>
      </div>

      {/* Image Section */}
      <div className="research-image-container">
        <img
          src={talent.imageUrl || `/assets/images/talents/default.png`}
          alt={talent.name}
          className="research-main-image"
        />
      </div>

      {/* Requirements Box - Show when there are requirements to display */}
      {(talent.parentTalentId || (talent.researchBuildingRequiredLevel && talent.researchBuildingRequiredLevel > 0)) && (
        <div className="requirements-container">
          <div className="requirements-header">
            <h5 className="requirements-title dark-yellow">
              {t("research:talents.requirements")}
            </h5>
          </div>
          <div className="requirements-list">
            {/* Parent Talent Requirement */}
            {talent.parentTalentId && (
              <div className="requirement-item">
                <div className="requirement-text-container">
                  <span className="requirement-bullet">•</span>
                  <span className={`requirement-text ${isParentRequirementMet ? "text-white" : "text-white"}`}>
                    {parentTalent?.name || t("research:talents.parentTalent")} (
                    {t("common:labels.level")} 1)
                  </span>
                </div>
                {isParentRequirementMet ? (
                  <img
                    src="/assets/images/icons/check.png"
                    alt="met"
                    className="requirement-icon requirement-checkmark"
                  />
                ) : (
                  <div className="requirement-go-btn-container">
                    <Button
                      variant="primary"
                      className="requirement-go-btn"
                      onClick={handleGoToParent}
                    >
                      {t("research:talents.go")}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Research Building Level Requirement */}
            {talent.researchBuildingRequiredLevel && talent.researchBuildingRequiredLevel > 0 && (
              <div className="requirement-item">
                <div className="requirement-text-container">
                  <span className="requirement-bullet">•</span>
                  <span className={`requirement-text ${isBuildingRequirementMet ? "text-white" : "text-danger"}`}>
                    {t("research:title")} ({t("common:labels.level")}{" "}
                    {talent.researchBuildingRequiredLevel})
                  </span>
                </div>
                {isBuildingRequirementMet ? (
                  <img
                    src="/assets/images/icons/check.png"
                    alt="met"
                    className="requirement-icon requirement-checkmark"
                  />
                ) : (
                  <div className="requirement-go-btn-container">
                    <Button
                      variant="primary"
                      className="requirement-go-btn"
                      onClick={handleGoToResearchBuilding}
                    >
                      {t("research:talents.go")}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Action Button - Only show when all requirements are met */}
      <div className="research-action-container justify-content-center d-flex flex-column">
        {canShowUpgradeButton && !isMaxLevel ? (
          <Button
            onClick={handleResearch}
            variant="wide-success"
            disabled={isUpgrading}
            style={{ width: "fit-content !important" }}
            className="research-btn"
            loading={isUpgrading}
          >
            <div className="research-btn-content">
              <span className="research-btn-text">
                {t("research:talents.research")}
              </span>
              <div className="research-cost">
                <span className="cost-amount">
                  {cost.cleg ?? "--"}
                </span>
                <img
                  src="/assets/images/icons/cleg.png"
                  alt="cost"
                  className="cost-icon"
                />
              </div>
            </div>
          </Button>
        ) : (
          <>
            {/* Show disabled button with grayed out appearance */}
            <Button
              variant="wide-success"
              disabled={true}
              className="research-btn research-btn-disabled"
            >
              <div className="research-btn-content">
                <span className="research-btn-text">
                  {t("research:talents.research")}
                </span>
                <div className="research-cost">
                  <span className="cost-amount">
                    {cost.cleg ?? "--"}
                  </span>
                  <img
                    src="/assets/images/icons/cleg.png"
                    alt="cost"
                    className="cost-icon"
                  />
                </div>
              </div>
            </Button>
            {/* Show disabled reason */}
            {finalDisabledReason && (
              <div className="disabled-reason-message">
                {finalDisabledReason}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );

  // Requirements State - Show unmet requirements
  const renderRequirementsState = () => (
    <div className="research-upgrade-content">
      {/* Header Section */}
      <div className="research-header">
        <h3 className="research-title dark-yellow">
          {talent.name} ({currentLevel}/{upgradeData.maxLevel})
        </h3>
        <p className="research-subtitle">
          {talent.description || t("research:talents.researchUpgrade")}
        </p>
      </div>

      {/* Progress Section */}
      <div className="research-progress-section">
        <div className="progress-info">
          <span className="dark-yellow">{t("common:labels.current")}:</span>
          <span className="text-white"> {currentEffect}</span>
        </div>
        <img
          src="/assets/images/icons/arrow-right.svg"
          alt="arrow"
          className="progress-arrow"
        />
        <div className="progress-info">
          <span className="text-success fw-bold">
            {t("research:talents.nextLevel")}:
          </span>
          <span className="text-white"> {nextEffect}</span>
        </div>
      </div>

      {/* Image Section */}
      <div className="research-image-container">
        <img src={talent.imageUrl || `/assets/images/talents/default.png`} alt={talent.name} className="research-main-image" />
      </div>

      {/* Requirements Box */}
      <div className="requirements-container">
        <div className="requirements-header">
          <h5 className="requirements-title dark-yellow">
            {t("research:talents.requirements")}
          </h5>
        </div>
        <div className="requirements-list">
          {upgradeData.requirements.length ? requirements.map((req, index) => (
            <div key={index} className="requirement-item">
              <div className="requirement-text-container">
                <span className="requirement-bullet">•</span>
                <span className={`requirement-text ${req.met ? "text-white" : "text-danger"}`}>
                  {req.name}
                </span>
              </div>
              {req.met ? (
                <img
                  src="/assets/images/icons/check.png"
                  alt="met"
                  className="requirement-icon"
                />
              ) : (
                <div className="requirement-go-btn-container">
                  <Button variant="primary" className="requirement-go-btn">
                    {t("research:talents.go")}
                  </Button>
                </div>
              )}
            </div>
          )) : (
            <div className="text-center text-muted py-3">
              {t("research:talents.noAdditionalRequirements")}
            </div>
          )}
        </div>
      </div>

      {/* Action Button - Only show when all requirements are met */}
      <div className="research-action-container">
        {canShowUpgradeButton && !isMaxLevel ? (
          <Button
            onClick={handleResearch}
            variant="wide-success"
            disabled={isUpgrading || !allRequirementsMet}
            className="research-btn"
            loading={isUpgrading}
          >
            <div className="research-btn-content">
              <span className="research-btn-text">
                {t("research:talents.research")}
              </span>
              <div className="research-cost">
                <span className="cost-amount">
                  {cost.cleg ?? "--"}
                </span>
                <img
                  src="/assets/images/icons/cleg.png"
                  alt="cost"
                  className="cost-icon"
                />
              </div>
            </div>
          </Button>
        ) : (
          <>
            {/* Show disabled button with grayed out appearance */}
            <Button
              variant="wide-success"
              disabled={true}
              className="research-btn research-btn-disabled"
            >
              <div className="research-btn-content">
                <span className="research-btn-text">
                  {t("research:talents.research")}
                </span>
                <div className="research-cost">
                  <span className="cost-amount">
                    {cost.cleg ?? "--"}
                  </span>
                  <img
                    src="/assets/images/icons/cleg.png"
                    alt="cost"
                    className="cost-icon"
                  />
                </div>
              </div>
            </Button>
            {/* Show disabled reason */}
            {finalDisabledReason && (
              <div className="disabled-reason-message">
                {finalDisabledReason}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );

  // Researching State - Progress bar
  const renderResearchingState = () => (
    <div className="research-upgrade-content">
      {/* Header Section */}
      <div className="research-header">
        <h3 className="research-title dark-yellow">
          {talent.name} ({currentLevel}/{upgradeData.maxLevel})
        </h3>
        <p className="research-subtitle">
          {talent.description || t("research:talents.researchUpgrade")}
        </p>
      </div>

      {/* Progress Section */}
      <div className="research-progress-section">
        <div className="progress-info">
          <span className="dark-yellow">{t("common:labels.current")}:</span>
          <span className="text-white"> {currentEffect}</span>
        </div>
        <img
          src="/assets/images/icons/arrow-right.svg"
          alt="arrow"
          className="progress-arrow"
        />
        <div className="progress-info">
          <span className="text-success fw-bold">
            {t("research:talents.nextLevel")}:
          </span>
          <span className="text-white"> {nextEffect}</span>
        </div>
      </div>

      {/* Image Section */}
      <div className="research-image-container">
        <img src={talent.imageUrl || `/assets/images/talents/default.png`} alt={talent.name} className="research-main-image" />
      </div>

      {/* Researching Box */}
      <div className="researching-container">
        <h5 className="researching-title text-white">
          {t("research:talents.researching")}
        </h5>
        <div className="researching-content">
          <div className="researching-image-wrapper">
            <img src={talent.imageUrl || `/assets/images/talents/default.png`} alt={talent.name} className="researching-image" />
          </div>
          <div className="researching-progress-container">
            <div className="research-level-progress" style={{ maxWidth: 280 }}>
              <div className="research-level-progress-frame" />
              <div className="research-level-progress-track">
                <div
                  className="research-level-progress-fill"
                  style={{ width: `${upgradingProgress?.progress ?? researchProgress ?? 0}%` }}
                />
              </div>
              <span className="research-level-progress-text">
                {upgradingProgress ? formatRemainingTime(upgradingProgress.remainingMs) : researchTime}
              </span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="researching-actions align-items-center">
          <Button
            onClick={onInstantUpgrade || handleInstant}
            variant="wide-primary"
            className="instant-btn bg-100 px-3  py-4"
          >
            {t("research:instant.action")}
            <div >
              <div className="d-flex align-items-center gap-1">
                <img
                  src="/assets/images/icons/cleg.png"
                  alt="gems"
                  className="instant-cost-icon"
                />
                <span>{upgradingProgress?.instantCost ?? cost.bloodGems}</span>
              </div>
            </div>
          </Button>

        </div>
      </div>
    </div>
  );

  // Completed State - Show success
  const renderCompletedState = () => (
    <div className="research-upgrade-content">
      {/* Header Section */}
      <div className="research-header">
        <h3 className="research-title dark-yellow">
          {talent.name} ({nextLevelNumber}/{upgradeData.maxLevel})
        </h3>
        <p className="research-subtitle">
          {talent.description || t("research:talents.researchUpgrade")}
        </p>
      </div>

      {/* Progress Section - Only Current for completed */}
      <div className="research-progress-section completed">
        <div className="progress-info">
          <span className="dark-yellow">{t("common:labels.current")}:</span>
          <span className="text-white"> {nextEffect}</span>
        </div>
      </div>

      {/* Image Section */}
      <div className="research-image-container">
        <img src={talent.imageUrl || `/assets/images/talents/default.png`} alt={talent.name} className="research-main-image" />
      </div>

      {/* Success Icon */}
      <div className="completed-icon-container">
        <div className="golden-victory-medal">
          <div className="medal-base">
            <div className="medal-center">
              <img
                src="/assets/images/icons/check.png"
                alt="success"
                className="checkmark-icon"
              />
            </div>
          </div>
          <div className="medal-ribbon"></div>
          <div className="sparkles">
            <div className="sparkle sparkle-1"></div>
            <div className="sparkle sparkle-2"></div>
            <div className="sparkle sparkle-3"></div>
            <div className="sparkle sparkle-4"></div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (dialogState) {
      case "requirements":
        return renderRequirementsState();
      case "researching":
        return renderResearchingState();
      case "completed":
        return renderCompletedState();
      default:
        return renderInfoState();
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        noFooter
        noTitle
        title={talent.name}
        size="lg"
        {...dialogProps}
      >
        {renderContent()}
      </Dialog>

      <style>{`
        .research-upgrade-content {
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 18px;
          align-items: center;
        }

        .research-header {
          background-color: rgba(0, 0, 0, 0.2);
          width: 100%;
          padding: 16px 24px;
          text-align: left;
        }

        .research-title {
          font-size: 18px;
          font-weight: 600;
          text-shadow: 0px 3px 0px #000000;
          margin: 0 0 6px 0;
        }

        .research-subtitle {
          font-size: 16px;
          color: #fff5e4;
          text-shadow: 0px 3px 0px #000000;
          margin: 0;
        }

        .research-progress-section {
          background-color: rgba(0, 0, 0, 0.2);
          width: 100%;
          padding: 12px 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 28px;
        }

        .progress-info {
          font-size: 16px;
          text-shadow: 0px 3px 0px #000000;
        }

        .progress-arrow {
          width: 20px;
          height: 20px;
          filter: invert(1);
        }

        .research-image-container {
          position: relative;
          overflow: hidden;
        }

        .research-main-image {
          border-radius: 12px;
          width: 480px;
          height: 300px;
          object-fit: cover;
        }

        .research-action-container {
          text-align: center;
          margin-top: 10px;
        }

        .research-btn {
          position: relative;
        }

        .research-btn-disabled {
          opacity: 0.6;
          filter: grayscale(50%);
          cursor: not-allowed;
        }

        .disabled-reason-message {
          margin-top: 10px;
          padding: 6px 14px;
          color: #4fc3f7;
          font-size: 14px;
          text-align: center;
          text-shadow: 0px 2px 4px rgba(0, 0, 0, 0.5);
          font-family: 'Changa', sans-serif;
        }

        .research-btn-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 5px;
        }

        .research-btn-text {
          font-size: 24px;
          font-weight: 500;
          text-shadow: 0px 3px 0px rgba(0, 0, 0, 0.25);
          line-height: 32px;
        }

        .research-cost {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: -12px;
        }

        .cost-amount {
          font-size: 20px;
          text-shadow: 0px 3px 0px #000000;
          color: #fff5e4;
        }

        .cost-icon {
          width: 30px;
          height: 30px;
          transform: scaleY(-1) rotate(180deg);
        }

        /* Requirements Styles */
        .requirements-container {
          width: 100%;
          background-color: rgba(0, 0, 0, 0.1);
          border: 1px solid rgba(0, 0, 0, 0.1);
          border-radius: 8px;
          overflow: hidden;
        }

        .requirements-header {
          background-color: rgba(0, 0, 0, 0.3);
          padding: 12px 15px
        }

        .requirements-title {
          font-size: 16px;
          margin: 0;
          text-shadow: 0px 3px 0px #000000;
        }

        .requirements-list {
          padding: 16px 12px;
          display: flex;
          flex-direction: column;
        }

        .requirement-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 6px 12px;
          margin: 0;
        }

        .requirement-text-container {
          display: flex;
          align-items: center;
          flex: 1;
        }

        .requirement-bullet {
          color: #fff5e4;
          font-size: 20px;
          font-weight: medium;
          margin-right: 10px;
          text-shadow: 0px 3px 0px #000000;
        }

        .requirement-text {
          font-size: 18px;
          text-shadow: 0px 3px 0px #000000;
          font-family: 'Changa', sans-serif;
          font-weight: medium;
        }

        .requirement-research-container {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          padding: 0 15px;
        }

        .requirement-research-text {
          display: flex;
          align-items: center;
        }

        .requirement-icon {
          width: 20px;
          height: 20px;
        }

        .requirement-checkmark {
          width: 24px;
          height: 24px;
          }

        .requirement-go-btn-container {
          flex-shrink: 0;
        }

        .requirement-go-btn {
          padding: 4px 16px;
          font-size: 12px;
          min-width: 75px;
          height: 38px;
        }

        /* Researching Styles */
        .researching-container {
          width: 100%;
          background-color: #332e2a;
          padding: 20px;
          border-radius: 8px;
        }

        .researching-title {
          text-align: center;
          font-size: 20px;
          font-weight: 600;
          text-shadow: 0px 3px 0px #000000;
          margin-bottom: 16px;
        }

        .researching-content {
          display: flex;
          align-items: center;
          gap: 20px;
          margin-bottom: 20px;
        }

        .researching-image-wrapper {
          flex-shrink: 0;
        }

        .researching-image {
          width: 100px;
          height: 100px;
          object-fit: cover;
          border-radius: 1.4px;
          border: 1.124px solid #c9c9c9;
        }

        .researching-progress-container {
          flex-grow: 1;
        }

        .researching-progress-bar {
          margin-bottom: 8px;
        }

        .progress-track {
          background-color: #444;
          border-radius: 8px;
          height: 14px;
          position: relative;
          overflow: hidden;
        }

        .progress-fill {
          background: linear-gradient(90deg, #ff880b 0%, #ffba27 100%);
          height: 100%;
          border-radius: 8px;
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .progress-text {
          color: white;
          font-size: 12px;
          font-weight: bold;
          position: absolute;
          right: 8px;
        }

        .researching-time {
          text-align: center;
          margin-top: 8px;
          font-size: 16.93px;
          margin-bottom: 0;
        }

        .researching-actions {
          display: flex;
          gap: 10px;
          justify-content: center;
        }

        .instant-btn {
          min-width: 89px;
          height: 43px;
          padding: 0;
          font-size: 16px;
        }

        .instant-cost-btn {
          min-width: 88px;
          height: 43px;
          padding: 0;
          background-color: rgba(0, 0, 0, 0.2);
          border-radius: 4px;
        }

        .instant-cost-icon {
          width: 25px;
          height: 25px;
        }

        /* Completed State Styles */
        .research-progress-section.completed {
          padding: 15px 35px;
        }

        .completed-icon-container {
          display: flex;
          justify-content: center;
          align-items: center;
          margin: 20px 0;
        }

        .golden-victory-medal {
          position: relative;
          width: 96px;
          height: 97px;
        }

        .medal-base {
          width: 96px;
          height: 97px;
          background: radial-gradient(circle, #ffd700 0%, #ffb000 70%, #cc8800 100%);
          border-radius: 50%;
          position: relative;
          border: 3px solid #cc8800;
          box-shadow: 
            0 0 10px rgba(255, 215, 0, 0.5),
            inset 0 0 20px rgba(255, 255, 255, 0.2);
        }

        .medal-center {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 60px;
          height: 60px;
          background: radial-gradient(circle, #ffffff 0%, #f0f0f0 70%, #cccccc 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px solid #cc8800;
        }

        .checkmark-icon {
          width: 24px;
          height: 24px;
          filter: brightness(0) saturate(100%) invert(27%) sepia(96%) saturate(1500%) hue-rotate(88deg);
        }

        .medal-ribbon {
          position: absolute;
          bottom: -15px;
          left: 50%;
          transform: translateX(-50%);
          width: 30px;
          height: 20px;
          background: linear-gradient(135deg, #cc0000 0%, #ff3333 50%, #cc0000 100%);
          clip-path: polygon(0% 0%, 100% 0%, 85% 100%, 50% 75%, 15% 100%);
        }

        .sparkles {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
        }

        .sparkle {
          position: absolute;
          width: 8px;
          height: 8px;
          background: #ffd700;
          border-radius: 50%;
          animation: sparkle 2s infinite ease-in-out;
        }

        .sparkle-1 {
          top: 10px;
          left: 10px;
          animation-delay: 0s;
        }

        .sparkle-2 {
          top: 15px;
          right: 5px;
          animation-delay: 0.5s;
        }

        .sparkle-3 {
          bottom: 20px;
          left: 5px;
          animation-delay: 1s;
        }

        .sparkle-4 {
          bottom: 15px;
          right: 10px;
          animation-delay: 1.5s;
        }

        @keyframes sparkle {
          0%, 100% {
            opacity: 0;
            transform: scale(0.5);
          }
          50% {
            opacity: 1;
            transform: scale(1);
          }
        }

        @media (max-width: 768px) {
          .research-upgrade-content {
            gap: 16px;
          }

          .research-header {
            padding: 12px 16px;
          }

          .research-title {
            font-size: 16px;
            text-shadow: 0px 2px 0px #000000;
            margin-bottom: 6px;
          }

          .research-subtitle {
            font-size: 14px;
            text-shadow: 0px 2px 0px #000000;
          }

          .research-progress-section {
            padding: 10px 16px;
            flex-direction: column;
            gap: 10px;
          }

          .progress-info {
            font-size: 14px;
            text-shadow: 0px 2px 0px #000000;
          }

          .progress-arrow {
            width: 18px;
            height: 18px;
          }

          .research-image-container {
            width: 100%;
            height: auto;
          }

          .research-main-image {
            width: 100%;
            height: auto;
            max-height: 250px;
          }

          .research-btn-text {
            font-size: 20px;
            line-height: 28px;
          }

          .cost-amount {
            font-size: 18px;
            text-shadow: 0px 2px 0px #000000;
          }

          .cost-icon {
            width: 28px;
            height: 28px;
          }

          .research-cost {
            margin-top: -10px;
          }

          .disabled-reason-message {
            font-size: 13px;
            padding: 6px 12px;
          }

          .requirements-header {
            padding: 10px 12px;
          }

          .requirements-title {
            font-size: 15px;
            text-shadow: 0px 2px 0px #000000;
          }

          .requirements-list {
            padding: 12px 10px;
          }

          .requirement-item {
            padding: 6px 10px;
            flex-direction: row;
            align-items: center;
          }

          .requirement-bullet {
            font-size: 18px;
            margin-right: 8px;
            text-shadow: 0px 2px 0px #000000;
          }

          .requirement-text {
            font-size: 16px;
            text-shadow: 0px 2px 0px #000000;
          }

          .requirement-checkmark {
            width: 22px;
            height: 22px;
          }

          .requirement-go-btn {
            padding: 4px 14px;
            font-size: 11px;
            min-width: 70px;
            height: 36px;
          }

          .researching-container {
            padding: 14px;
          }

          .researching-title {
            font-size: 18px;
            text-shadow: 0px 2px 0px #000000;
            margin-bottom: 14px;
          }

          .researching-content {
            flex-direction: column;
            gap: 14px;
            margin-bottom: 14px;
          }

          .researching-image {
            width: 90px;
            height: 90px;
          }

          .progress-text {
            font-size: 10px;
          }

          .researching-time {
            font-size: 14px;
          }

          .instant-btn {
            min-width: 75px;
            height: 38px;
            font-size: 14px;
          }

          .instant-cost-btn {
            min-width: 75px;
            height: 38px;
            font-size: 14px;
          }

          .instant-cost-icon {
            width: 20px;
            height: 20px;
          }

          .golden-victory-medal {
            width: 80px;
            height: 80px;
          }

          .medal-base {
            width: 80px;
            height: 80px;
          }

          .medal-center {
            width: 50px;
            height: 50px;
          }

          .checkmark-icon {
            width: 20px;
            height: 20px;
          }
        }

        @media (max-width: 480px) {
          .research-header {
            padding: 10px 12px;
          }

          .research-title {
            font-size: 14px;
          }

          .research-subtitle {
            font-size: 12px;
          }

          .progress-info {
            font-size: 12px;
          }

          .research-main-image {
            max-height: 200px;
          }

          .research-btn-text {
            font-size: 18px;
            line-height: 24px;
          }

          .cost-amount {
            font-size: 16px;
          }

          .cost-icon {
            width: 24px;
            height: 24px;
          }

          .requirement-text {
            font-size: 14px;
          }

          .requirement-bullet {
            font-size: 16px;
          }

          .researching-title {
            font-size: 16px;
          }

          .researching-image {
            width: 75px;
            height: 75px;
          }
        }
      `}</style>
    </>
  );
};

export default ResearchUpgradeDialog;
